import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Transactions & Fees',
    tagline: 'Inputs, outputs, and a fee that\'s never actually a field.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - how the transaction fee is actually determined (inputs minus outputs)
//   Q1 - what the mempool is
//   Q2 - why more confirmations mean stronger practical finality
export const blocks: StageBlock[] = [
    content('anatomy', () => (
        <StageSection gradientBorder eyebrow="Stage 4 of 8" title="What's inside a transaction">
            <Prose>
                A transaction references the UTXOs it's spending as inputs, and declares the new UTXOs it's creating
                as outputs. Once built and signed, it's broadcast to the network, where it waits to be picked up and
                included in a block.
            </Prose>
            <FactGrid items={[
                ['Inputs', 'References to existing UTXOs being spent, plus the signature/data proving the right to spend them.'],
                ['Outputs', 'New UTXOs being created: an amount, and the locking condition required to spend it later.'],
            ]} />
        </StageSection>
    )),

    content('fees', () => (
        <StageSection title="The fee is a leftover, not a line item">
            <Prose>
                There's no dedicated "fee" field in a transaction. The fee is simply whatever's left over: the total
                value of the inputs minus the total value of the outputs. Whoever mines the block containing the
                transaction keeps that difference. Because block space is limited, fees function as a real market:
                a higher fee per byte gets prioritized when many transactions are competing for room in the next
                block.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('mempool', () => (
        <StageSection title="The mempool: a waiting room for transactions">
            <Prose>
                Once broadcast, a valid transaction sits in each node's <strong>mempool</strong>, its local set of
                transactions it has seen but that haven't been mined into a block yet. Different nodes can have
                slightly different mempools at any given moment, since they don't all receive every transaction at
                exactly the same time.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('confirmations', () => (
        <StageSection title="Confirmations: finality that gets stronger over time">
            <Prose>
                A transaction becomes part of the ledger once it's mined into a block. Each additional block mined
                on top of it is called a confirmation, and each one makes reversing that transaction more expensive:
                an attacker would have to redo the proof-of-work for that block and every block after it, faster
                than the honest network can extend the real chain. More confirmations mean stronger practical
                finality, which is why exchanges and merchants often wait for several before treating a payment as
                settled.
            </Prose>
            <KeyPoints items={[
                <>A transaction's outputs are UTXOs, and its inputs consume existing ones: the model from the previous stage in action.</>,
                <>The next stage looks at what actually locks an output: it's not just "an address," it's a small program.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
